import { useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ListBiddingSpotExecutionResultsResponse } from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  PrimaryButton,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { Modal } from '~/components/common/Modal'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { downloadCsv } from '~/libs/downloadCsv'
import type { CivilDate } from '~/models/civilDate'
import {
  type DeliveryDatePeriod,
  isValidDeliveryDatePeriod,
} from '~/models/deliveryDatePeriod'
import {
  buildExecutionResultCsv,
  spotExecutionResultCsvFilename,
} from '~/models/executionResultCsv'
import { useListSpotExecutionResults } from '~/pages/SpotBidding/hooks/useListSpotExecutionResults'
import type { SpotExecutionResultFilter } from '~/pages/SpotBidding/models/spotExecutionResult'
import {
  spotExecutionResultRequest,
  toExecutionResultCsvLines,
} from '~/pages/SpotBidding/models/spotExecutionResultQuery'

type Props = {
  isOpen: boolean
  onClose: () => void
  /** 期間の初期値 (開始・終了とも画面で選んでいる受渡日)。 */
  deliveryDate: CivilDate
  filter: SpotExecutionResultFilter
  areaCodes: readonly AreaCode[]
}

type Pending = {
  period: DeliveryDatePeriod
  response: ListBiddingSpotExecutionResultsResponse
}

/**
 * 約定結果 CSV の期間指定ダイアログ (AC-BSP-115)。 期間は開始・終了の受渡日を含み 31日以内。
 * 約定結果が未着の札があれば件数を警告し、 続行すると同じ応答から CSV を出す (再取得しない)。
 * 取得中は期間を変えられず、 要求後に期間が変わった・閉じた応答は捨てる (古い期間の CSV を出さない)。
 */
export const ExecutionResultCsvDialog = ({
  isOpen,
  onClose,
  deliveryDate,
  filter,
  areaCodes,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [from, setFrom] = useState<CivilDate | undefined>(deliveryDate)
  const [to, setTo] = useState<CivilDate | undefined>(deliveryDate)
  const [periodError, setPeriodError] = useState(false)
  const [pending, setPending] = useState<Pending | undefined>(undefined)
  const list = useListSpotExecutionResults()
  const resolveCreatedBy = useMemberDisplayName()
  const latestRequest = useRef(0)

  const close = (): void => {
    latestRequest.current += 1
    setPending(undefined)
    setPeriodError(false)
    list.reset()
    onClose()
  }

  const download = ({ period, response }: Pending): void => {
    downloadCsv(
      spotExecutionResultCsvFilename(period.from, period.to),
      buildExecutionResultCsv(toExecutionResultCsvLines(response.lines), {
        resolveCreatedBy,
      }),
    )
    close()
  }

  const submit = (): void => {
    if (from === undefined || to === undefined) {
      setPeriodError(true)
      return
    }
    const period = { from, to }
    if (!isValidDeliveryDatePeriod(period)) {
      setPeriodError(true)
      return
    }
    setPeriodError(false)
    latestRequest.current += 1
    const requestId = latestRequest.current
    list.mutate(spotExecutionResultRequest(period, filter, areaCodes), {
      onSuccess: (response) => {
        if (requestId !== latestRequest.current) return
        if (response.unarrivedBidCount > 0) {
          setPending({ period, response })
          return
        }
        download({ period, response })
      },
    })
  }

  const changePeriod = (next: () => void): void => {
    next()
    latestRequest.current += 1
    setPending(undefined)
  }

  return (
    <Modal
      footer={
        <>
          <TertiaryButton marginRight={3} onClick={close}>
            {t('common:action.cancel')}
          </TertiaryButton>
          {pending !== undefined ? (
            <PrimaryButton onClick={() => download(pending)}>
              {t('page:biddingSpot.executionResult.csvDialog.continue')}
            </PrimaryButton>
          ) : (
            <PrimaryButton loading={list.isPending} onClick={submit}>
              {t('page:biddingSpot.executionResult.csvDialog.download')}
            </PrimaryButton>
          )}
        </>
      }
      isOpen={isOpen}
      onClose={close}
      size="sm"
      title={t('page:biddingSpot.executionResult.csvDialog.title')}
    >
      <VStack align="stretch" gap={3}>
        {list.isError && (
          <Alert
            status="error"
            title={t('page:biddingSpot.executionResult.csvDialog.loadError')}
          />
        )}
        {pending !== undefined && (
          <Alert
            status="warning"
            title={t(
              'page:biddingSpot.executionResult.csvDialog.unarrivedWarning',
              { count: pending.response.unarrivedBidCount },
            )}
          />
        )}
        <Flex alignItems="center" gap={2}>
          <CompactCivilDateField
            aria-label={t('page:biddingSpot.executionResult.csvDialog.from')}
            disabled={list.isPending}
            hasError={periodError}
            id="spot-execution-result-csv-from"
            onChange={(d) => changePeriod(() => setFrom(d))}
            value={from}
          />
          <Text>〜</Text>
          <CompactCivilDateField
            aria-label={t('page:biddingSpot.executionResult.csvDialog.to')}
            disabled={list.isPending}
            hasError={periodError}
            id="spot-execution-result-csv-to"
            onChange={(d) => changePeriod(() => setTo(d))}
            value={to}
          />
        </Flex>
        {periodError && (
          <Text color="Text.negativeHigh" fontSize="sm">
            {t('page:biddingSpot.executionResult.csvDialog.periodInvalid')}
          </Text>
        )}
      </VStack>
    </Modal>
  )
}
